import { useMemo } from 'react';
import difference from 'lodash/difference';
import { Listbox } from '@headlessui/react';
import useTranslation from 'next-translate/useTranslation';

// Components
import Button from '@/components/base/Button';
import SelectFilter from '@/components/base/SelectFilter';
import Icon from '@/components/base/Icon';
import Checkbox from '@/components/base/Checkbox';

function SelectColumnOption({ option, selected }) {
    return (
        <div
            className="flex items-center space-x-2"
            data-testid={`option-select-table-settings-${option.value}`}>
            <Checkbox checked={selected} readOnly />
            <span>{option.label}</span>
        </div>
    );
}

function SelectColumnButton({ buttonText }) {
    return (
        <Button
            as={Listbox.Button}
            variant="whiteNoBorder"
            className="w-full justify-between space-x-2"
            size="sm">
            <div className="space-x-2">
                <Icon icon="settings" />
                <span className="text-icoBlack dark:text-white">{buttonText}</span>
            </div>
            <Icon icon="chevron-bottom" />
        </Button>
    );
}

function SelectTableColumns({ columns, visibleColumns, setColumnVisibility, buttonText }) {
    const { t } = useTranslation();
    const visibleColumnsIds = visibleColumns.map((column) => column.id);
    const columnsOptions = useMemo(
        () =>
            columns.reduce((current, column) => {
                if (column.getCanHide()) {
                    return [
                        ...current,
                        {
                            value: column.id,
                            label: t(column.columnDef.header().props.i18nKey)
                        }
                    ];
                }
                return current;
            }, []),
        [columns, t]
    );

    const selectedOptions = columnsOptions.filter((column) =>
        visibleColumnsIds.includes(column.value)
    );

    const handleSelectColumns = (newSelectedOptions) => {
        if (newSelectedOptions.length > selectedOptions.length) {
            const newColumnId = newSelectedOptions[newSelectedOptions.length - 1].value;
            return setColumnVisibility((prevState) => ({ ...prevState, [newColumnId]: true }));
        }

        const removedColumns = difference(selectedOptions, newSelectedOptions);
        if (removedColumns.length) {
            const removedColumnId = removedColumns[0].value;
            setColumnVisibility((prevState) => ({ ...prevState, [removedColumnId]: false }));
        }
    };

    return (
        <SelectFilter
            selected={selectedOptions}
            noValue
            noBorder
            isMultiple
            setSelected={handleSelectColumns}
            optionsWrapperClassName="right-0"
            idInput="select-filter-collaboration-settings"
            options={columnsOptions}
            button={<SelectColumnButton buttonText={buttonText} />}>
            <SelectColumnOption />
        </SelectFilter>
    );
}

export default SelectTableColumns;
